import React, { forwardRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';
const buttonVariants = cva('inline-flex items-center justify-center font-medium transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-atios-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-40 active:translate-y-[1px]', {
  variants: {
    variant: {
      // Primary - ATIOS Blue (use sparingly)
      primary: 'bg-atios-primary text-white hover:bg-atios-hover shadow-xs',
      // Secondary - Neutral (preferred for most actions)
      secondary: 'bg-white text-neutral-700 border border-neutral-300 hover:bg-neutral-50 hover:border-neutral-400 shadow-xs',
      // Ghost - Minimal (for tertiary actions)
      ghost: 'text-neutral-700 hover:bg-neutral-100',
      // Destructive - Red (for dangerous actions)
      destructive: 'bg-error text-white hover:bg-error-dark shadow-xs',
      // Link - Text only
      link: 'text-atios-primary hover:text-atios-secondary underline-offset-4 hover:underline'
    },
    size: {
      sm: 'h-8 px-3 text-xs rounded-md',
      md: 'h-9 px-4 text-sm rounded-md',
      lg: 'h-10 px-5 text-sm rounded-md',
      icon: 'h-9 w-9 rounded-md'
    },
    fullWidth: {
      true: 'w-full'
    }
  },
  defaultVariants: {
    variant: 'primary',
    size: 'md',
    fullWidth: false
  }
});
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}
const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  className,
  variant,
  size,
  fullWidth,
  asChild = false,
  ...props
}, ref) => {
  const Comp = asChild ? Slot : 'button';
  return <Comp className={cn(buttonVariants({
    variant,
    size,
    fullWidth,
    className
  }))} ref={ref} {...props} data-id="element-333" />;
});
Button.displayName = 'Button';
export { Button, buttonVariants };